import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { findDegreesTemplates } from '../data/findDegreesTemplates';
import { degreeCategories } from '../data/degrees';
import { Logo } from './Logo';
import { jumpToTop } from '../utils/smoothScroll';
function slugToLabel(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
export function Footer() {
  const currentYear = new Date().getFullYear();
  const [isFindDegreesOpen, setIsFindDegreesOpen] = useState(false);
  const [isAreasOpen, setIsAreasOpen] = useState(false);
  // Every footer link lands the visitor at the top of the destination page.
  const scrollToTop = jumpToTop;
  return <footer className="bg-navy-dark text-white pt-16 pb-8" style={{
    backgroundColor: "#5143A3"
  }} data-id="element-306" data-mp="3qf9rorxsn4hw">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-307" data-mp="2a1jk3b7gty3i">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-308" data-mp="2ezuvvrhbmiod">
          {/* Brand Col */}
          <div className="col-span-1 md:col-span-1" data-id="element-309" data-mp="3la29euiur3v8">
            <Link to="/" onClick={scrollToTop} className="flex items-center mb-4" aria-label="findonlinecolleges.org home" data-id="element-310" data-mp="3gapfjptkcwj5">
              <Logo variant="light" data-id="element-311" data-mp="3v8rgds97n3wp" />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-312" data-mp="3q8mzw28ay96h">
              Empowering students to find the perfect online degree program to
              advance their careers and achieve their goals.
            </p>
          </div>

          {/* Links Col 1 */}
          <div data-id="element-313" data-mp="33nmw76ipll8">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-3" data-id="element-314" data-mp="323239a09h0k">
              Areas of Study
            </h3>
            {/* A dropdown rather than a list: every area is reachable without
                the column running the height of the footer. */}
            <div className="relative mb-6" data-id="element-315" data-mp="qtrns6sbn78j">
              <button type="button" onClick={() => {
              setIsAreasOpen(open => !open);
              setIsFindDegreesOpen(false);
            }} aria-expanded={isAreasOpen} aria-haspopup="listbox" className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-gray-200 transition-colors" data-id="element-316" data-mp="msg8ov72q4el">
                <span data-id="element-317" data-mp="1clmaprbb17iz">Browse areas of study</span>
                <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isAreasOpen ? 'rotate-180' : ''}`} data-id="element-318" data-mp="1ckt20kssc7y7" />
              </button>

              {isAreasOpen && <ul role="listbox" className="absolute z-20 left-0 right-0 mt-2 max-h-72 overflow-y-auto bg-navy-dark border border-white/10 rounded-md shadow-xl" data-id="element-319" data-mp="1mhpfl4xjjk9m">
                  {degreeCategories.map(category => <li key={category.id} data-id="element-320" data-mp="3tarr8xfp7jny">
                      <Link to={`/degrees/${category.id}`} onClick={() => {
                  setIsAreasOpen(false);
                  scrollToTop();
                }} className="block px-3 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors" data-id="element-321" data-mp="2aevdxsrcbo5s">
                        {category.name}
                      </Link>
                    </li>)}
                </ul>}
            </div>

            {/* Find Degrees Dropdown */}
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-3" data-id="element-322" data-mp="2kdb8y6thnhkk">
              Find Degrees
            </h3>
            <div className="relative" data-id="element-323" data-mp="2fd5bub4g92v3">
              <button type="button" onClick={() => {
              setIsFindDegreesOpen(open => !open);
              setIsAreasOpen(false);
            }} aria-expanded={isFindDegreesOpen} aria-haspopup="listbox" className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-gray-200 transition-colors" data-id="element-324" data-mp="2fgayzktipuo6">
                <span data-id="element-325" data-mp="2wnr46yk2b17p">Search interests</span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isFindDegreesOpen ? 'rotate-180' : ''}`} data-id="element-326" data-mp="2ivkyy7ig6en1" />
              </button>

              {isFindDegreesOpen && <ul role="listbox" className="absolute z-20 left-0 right-0 mt-2 bg-navy-dark border border-white/10 rounded-md shadow-xl overflow-hidden" data-id="element-327" data-mp="2nrrxvgcfepvh">
                  {findDegreesTemplates.length === 0 && <li className="px-3 py-2 text-xs text-gray-500 italic" data-id="element-328" data-mp="3fizbhtkzdrfs">
                      No themes available yet
                    </li>}
                  {findDegreesTemplates.map(template => <li key={template.slug} data-id="element-329" data-mp="36isuhxq2q9bv">
                      <Link to={`/find-degrees/${template.slug}`} onClick={() => {
                  setIsFindDegreesOpen(false);
                  scrollToTop();
                }} className="block px-3 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors" data-id="element-330" data-mp="1q5uhbohlwum6">
                        {slugToLabel(template.slug)}
                      </Link>
                    </li>)}
                </ul>}
            </div>
          </div>

          {/* Links Col 2 */}
          <div data-id="element-331" data-mp="1wk9sf1z9a11n">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-332" data-mp="1rnsu5eyce6au">
              Resources
            </h3>
            <ul className="space-y-3" data-id="element-333" data-mp="13z05m3k1v0q9">
              <li data-id="element-334" data-mp="13vvwcrrq9esq">
                <Link to="/colleges" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-335" data-mp="zwd6rioyofjk">
                  Partner Colleges
                </Link>
              </li>
              <li data-id="element-336" data-mp="18xbeoc2it77f">
                <Link to="/request-information" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-337" data-mp="18wt92otyxgmu">
                  Request Information
                </Link>
              </li>
              <li data-id="element-338" data-mp="g7meu21jrd7e">
                <Link to="/authors" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-339" data-mp="g848qs0qlfc5">
                  Our Authors
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Col */}
          <div data-id="element-340" data-mp="2q06t5dc2vqxr">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-341" data-mp="2q1rlqxeu0zsz">
              Legal
            </h3>
            <ul className="space-y-3" data-id="element-342" data-mp="2v1md122irrek">
              <li data-id="element-343" data-mp="1xcawu9qf811i">
                <Link to="/privacy-policy" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-344" data-mp="1xct2fwyz3rm3">
                  Privacy Policy
                </Link>
              </li>
              <li data-id="element-345" data-mp="2795wwx2ce9hw">
                <Link to="/terms-and-conditions" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-346" data-mp="278nrb9tsiixb">
                  Terms of Service
                </Link>
              </li>
              <li data-id="element-347" data-mp="3irgobf76x7wp">
                <Link to="/cookie-policy" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-348" data-mp="3mqft8zsx94o5">
                  Cookie Policy
                </Link>
              </li>
              <li data-id="element-349" data-mp="1718wermeor7d">
                <Link to="/do-not-sell-my-info" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-350" data-mp="172trqjgjoe6a">
                  Do Not Sell My Info
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8" data-id="element-351" data-mp="scurmb5fiso6">
          <div className="text-xs text-gray-400 space-y-4 text-center md:text-left" data-id="element-352" data-mp="sefow8j4oppq">
            <p data-id="element-353" data-mp="nd05sm0igt9z">
              <strong data-id="element-354" data-mp="ncgkcvbnjmr7">Advertiser Disclosure:</strong> FindOnlineColleges.org is
              an advertising-supported site. Featured or trusted partner
              programs and all school search, finder, or match results are for
              schools that compensate us. This compensation does not influence
              our school rankings, resource guides, or other
              editorially-independent information published on this site.
            </p>
            <p data-id="element-355" data-mp="1hlicosx5o528">
              <strong data-id="element-356" data-mp="1hn38elv7hu1o">TCPA Disclosure:</strong> By submitting your information
              on this website, you consent to be contacted by our educational
              partners regarding their programs via email, telephone, or text
              message.
            </p>
            <p className="pt-4 text-gray-500" data-id="element-357" data-mp="1cuig5ory7p09">
              &copy; {currentYear} FindOnlineColleges.org. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>;
}